/**
 * Unicode-aware string utilities.
 *
 * JavaScript's built-in `.charAt(0)`, `str[0]`, and `.split('')` operate on
 * UTF-16 code units, which means multi-byte characters (emoji, non-BMP glyphs,
 * combining marks) are silently broken.  These helpers use `Intl.Segmenter` to
 * work with proper Unicode grapheme clusters instead.
 */

const _segmenter = new Intl.Segmenter();

/**
 * Returns the first Unicode grapheme cluster of `str`.
 *
 * ```ts
 * getFirstGrapheme('😀abc')  // '😀'  (not '\ud83d')
 * getFirstGrapheme('Åsa')    // 'Å'
 * getFirstGrapheme('')       // ''
 * ```
 */
export function getFirstGrapheme(str: string): string {
  const first = _segmenter.segment(str)[Symbol.iterator]().next().value;
  return first?.segment ?? '';
}

/**
 * Derives uppercase initials from a display name.
 * Splits on whitespace and takes the first grapheme of each word.
 *
 * ```ts
 * getInitials('Jean-Luc Picard')  // 'JP'
 * getInitials('Åsa Nilsson')      // 'ÅN'
 * getInitials('😀 Smith')         // '😀S'
 * getInitials('Alice')            // 'A'
 * ```
 */
export function getInitials(name: string, maxLength: number = 2): string {
  const firstGraphemes = name
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => getFirstGrapheme(word))
    .filter((g) => g.length > 0)
    .slice(0, maxLength);

  return firstGraphemes.join('').toUpperCase();
}
